import { useState, type FormEvent } from 'react';
import { readLastBusinessSlug, saveLastBusinessSlug } from '../utils/lastBusiness';
import { ApiError, getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Brand } from '../components/Brand';
import { continueSignup, SelfServiceApiError } from '../selfservice/api';
import { signupHandoff } from '../selfservice/handoff';
import { usePublicConfig } from '../selfservice/usePublicConfig';
import { isValidPassword } from '../utils/passwordPolicy';
import { InfoDialog, type InfoTopic } from '../info/InfoDialog';
import { publicSupportContext } from '../info/supportInfo';
import { useInfoDialogBrowserBack } from '../info/useInfoDialogBrowserBack';

/**
 * ONB-001: reanudar un registro desde el inicio de sesión. Solo cuando el login respondió «credenciales
 * inválidas», el registro está abierto, se escribió un correo y no se eligió negocio. ONB-002: si las
 * credenciales son las de un registro a medias, el servidor deja la sesión corta de registro (cookie
 * httpOnly) y la contraseña no pasa a ninguna otra pantalla. ONB-003: si el correo ya se confirmó en otro
 * lado, la contraseña sola no basta: lleva a «Abre el enlace en este equipo» (el servidor envió uno nuevo
 * al correo). Devuelve si se redirigió.
 */
async function resumeSignup(loginError: unknown, signupEnabled: boolean, username: string, password: string, businessSlug: string): Promise<boolean> {
  const invalidCredentials = loginError instanceof ApiError && loginError.status === 401;
  if (!signupEnabled || !invalidCredentials || businessSlug.trim() || !username.includes('@')) return false;
  try {
    const result = await continueSignup(username.trim(), password);
    if (result.status === 'EMAIL_VERIFIED') {
      signupHandoff.set({ status: 'EMAIL_VERIFIED', email: result.email });
      window.location.hash = 'signup/setup';
      return true;
    }
    if (result.status === 'PENDING_EMAIL_VERIFICATION') {
      signupHandoff.set({ status: 'PENDING_EMAIL_VERIFICATION', email: result.email, showCheckEmail: true });
      window.location.hash = 'signup/continue';
      return true;
    }
  } catch (error) {
    if (error instanceof SelfServiceApiError && error.code === 'SIGNUP_LINK_REQUIRED') {
      signupHandoff.set({ status: 'EMAIL_VERIFIED', email: username.trim().toLowerCase(), linkRequired: true });
      window.location.hash = 'signup/continue';
      return true;
    }
    // No es un registro a medias (o el servidor no respondió): se muestra el error del login.
  }
  return false;
}

export function LoginPage({ notice }: { notice?: string }) {
  const { login } = useAuth();
  // Fase 27B: el CTA "Crear cuenta" solo aparece si el backend habilita el registro autoservicio
  // (gate ON + entrega de email disponible). El backend es la fuente de verdad; esto solo evita
  // ofrecer un flujo que sería rechazado.
  const { config } = usePublicConfig();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [businessSlug, setBusinessSlug] = useState(readLastBusinessSlug);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [info, setInfo] = useState<InfoTopic | null>(null);
  // Codex R2-03: en el Chrome del teléfono, Atrás cierra la ayuda sin salir del inicio de sesión.
  useInfoDialogBrowserBack(info !== null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const validationError = validateLogin(username, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await login({
        username: username.trim(),
        password,
        ...(businessSlug.trim()
          ? { businessSlug: businessSlug.trim().toLowerCase() }
          : {}),
      });
      saveLastBusinessSlug(businessSlug);
    } catch (requestError) {
      // ONB-001: quien se registró pero todavía no prepara su negocio no tiene usuario: el login lo
      // rechaza. Con el registro abierto, se prueba si esas credenciales son de un registro a medias
      // y se le lleva a donde se quedó. Si no, se dice el error del login, sin pistas extra.
      if (await resumeSignup(requestError, config?.signupEnabled === true, username, password, businessSlug)) return;
      setError(getErrorMessage(requestError, 'No fue posible iniciar sesión.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-visual" aria-label="Negocio360">
        <Brand />
        <div className="auth-visual__content">
          <span className="eyebrow eyebrow--light">Control de negocio</span>
          <h1>Tu operación, clara y en un solo lugar.</h1>
          <p>
            Consulta ventas, compras, costos e inventario de forma sencilla.
          </p>
        </div>
        <div className="auth-visual__footer">Acceso seguro para tu negocio</div>
      </section>

      <section className="auth-form-panel">
        <div className="auth-form-card">
          <span className="eyebrow">Bienvenido</span>
          <h2>Inicia sesión</h2>
          <p className="form-intro">Ingresa con los datos de tu negocio.</p>

          {notice && <div className="alert alert--success">{notice}</div>}
          {error && <div className="alert alert--error">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <label className="field">
              <span>Negocio <small>Opcional si tu usuario es único</small></span>
              <input
                autoComplete="organization"
                disabled={isSubmitting}
                onChange={(event) => setBusinessSlug(event.target.value)}
                placeholder="tu-negocio"
                value={businessSlug}
              />
            </label>

            <label className="field">
              <span>Usuario</span>
              <input
                autoComplete="username"
                autoFocus
                disabled={isSubmitting}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Tu usuario"
                value={username}
              />
            </label>

            <label className="field">
              <span>Contraseña</span>
              <input
                autoComplete="current-password"
                disabled={isSubmitting}
                // ONB-002: quien entra con su correo (cuentas del registro) tiene una contraseña con letras;
                // con un usuario sin correo (empleados) sigue siendo un PIN: teclado numérico.
                inputMode={username.includes('@') ? 'text' : 'numeric'}
                maxLength={72}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Tu contraseña"
                type="password"
                value={password}
              />
            </label>

            <button className="button button--primary button--wide" disabled={isSubmitting}>
              {isSubmitting ? 'Ingresando...' : 'Entrar'}
            </button>
          </form>

          <p className="auth-help">
            <a className="text-button" href="#forgot-password" onClick={() => { window.location.hash = 'forgot-password'; }}>¿Olvidaste tu contraseña?</a>
          </p>
          {config?.signupEnabled && (
            <p className="auth-help">
              ¿No tienes cuenta?{' '}
              <a className="text-button" href="#signup" onClick={() => { window.location.hash = 'signup'; }}>Crear cuenta</a>
            </p>
          )}
          {/* ONB-001: la reanudación no depende de cómo responda el login (un correo que ya es dueño de otro
              negocio, o un negocio recordado, no la ofrecerían): siempre hay un camino explícito. */}
          {config?.signupEnabled && (
            <p className="auth-help">
              ¿Te registraste y no terminaste?{' '}
              <a className="text-button" href="#signup/continue" onClick={() => { window.location.hash = 'signup/continue'; }}>Continúa tu registro</a>
            </p>
          )}
          <p className="auth-help">Tu sesión se protege y valida con el backend.</p>
          {/* P-085: ayuda y textos legales en un diálogo, sin salir del inicio de sesión. */}
          <p className="auth-help">
            <button className="text-button" onClick={() => setInfo('help')} type="button">Ayuda</button>
            {' · '}
            <button className="text-button" onClick={() => setInfo('privacy')} type="button">Privacidad</button>
            {' · '}
            <button className="text-button" onClick={() => setInfo('terms')} type="button">Términos</button>
          </p>
          {info && <InfoDialog context={publicSupportContext(window.location.hash)} initial={info} onClose={() => setInfo(null)} />}
        </div>
      </section>
    </main>
  );
}

function validateLogin(username: string, password: string): string | null {
  if (!username.trim()) return 'El usuario es requerido.';
  if (!password) return 'La contraseña es requerida.';
  // ONB-002: un PIN de 4 a 8 números o la contraseña de una cuenta del registro (8+ con letras y números).
  if (!isValidPassword(password)) {
    return 'La contraseña es un PIN de 4 a 8 números, o tiene al menos 8 caracteres con letras y números.';
  }
  return null;
}
